import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/portfolio/Reveal";
import { Lightbox } from "@/components/portfolio/Lightbox";
import { mediaUrl, api } from "@/lib/api";
import { WA_LINK } from "@/lib/site";

function VideoCard({ video, onOpen, featured = false }) {
  const videoRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const isVertical = video.format === "vertical";

  const handleEnter = () => {
    setHovering(true);
    const el = videoRef.current;
    if (el) {
      el.play().catch(() => {});
    }
  };
  const handleLeave = () => {
    setHovering(false);
    const el = videoRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={isVertical ? (featured ? "col-span-2 md:col-span-1" : "") : "col-span-2"}
    >
      <button
        onClick={() => onOpen(video)}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        data-testid={`video-card-${video.id}`}
        className={`video-card group relative block w-full text-left overflow-hidden bg-cream-dark rounded-2xl md:rounded-3xl ${
          isVertical ? (featured ? "aspect-[4/5] md:aspect-[3/4]" : "aspect-[3/4]") : "aspect-video"
        }`}
      >
      {!imgLoaded && <div className="absolute inset-0 bg-cream-dark animate-pulse" data-testid={`video-skeleton-${video.id}`} />}
      {video.thumbnail_url && (
        <img
          src={mediaUrl(video.thumbnail_url)}
          alt={video.title}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter,transform,opacity] duration-500 ${hovering ? "opacity-0" : "opacity-100"}`}
        />
      )}
      <video
        ref={videoRef}
        src={mediaUrl(video.video_url)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hovering ? "opacity-100" : "opacity-0"}`}
        muted
        loop
        playsInline
        preload="none"
      />
      {(video.categories?.length ? video.categories : [video.category]).filter(Boolean).length > 0 && (
        <span className="absolute top-3 left-3 bg-cream/95 text-ink text-[10px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full" data-testid={`video-tag-${video.id}`}>
          {(video.categories?.length ? video.categories : [video.category]).filter(Boolean).join(" · ")}
        </span>
      )}
      <span className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-cream/90">
        <ArrowUpRight className="w-4 h-4" />
      </span>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className={`w-10 h-10 md:w-12 md:h-12 rounded-full bg-cream flex items-center justify-center transition-transform duration-300 ${hovering ? "scale-90" : ""}`}>
          <Play className="w-3.5 h-3.5 md:w-4 md:h-4 text-ink fill-ink" />
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 md:p-5 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent">
        {video.brand && <p className="text-cream/80 text-[9px] md:text-[10px] uppercase tracking-[0.16em] truncate">{video.brand}</p>}
        <p className="text-cream font-serif text-sm md:text-lg leading-snug mt-0.5 line-clamp-2">{video.title}</p>
      </div>
      </button>
      {video.description && (
        <p className="mt-3 text-sm text-ink-soft leading-relaxed" data-testid={`video-desc-${video.id}`}>{video.description}</p>
      )}
    </motion.div>
  );
}

export function VideoWall({ videos, loading }) {
  const safeVideos = useMemo(() => (Array.isArray(videos) ? videos : []), [videos]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
  api.get("/folders")
    .then(({ data }) => {
      setFolders(Array.isArray(data) ? data : []);
    })
    .catch(() => {
      setFolders([]);
    });
}, []);

  const categories = useMemo(() => {
    const cats = [...new Set(safeVideos.flatMap((v) => (v.categories && v.categories.length ? v.categories : [v.category]).filter(Boolean)))];
    return ["All", ...cats, "Vertical", "Horizontal"];
   }, [safeVideos]);

  const filtered = useMemo(() => {
    if (filter === "All") return safeVideos;
    if (filter === "Vertical") return safeVideos.filter((v) => v.format === "vertical");
   if (filter === "Horizontal") return safeVideos.filter((v) => v.format === "horizontal");
    return safeVideos.filter((v) => (v.categories && v.categories.length ? v.categories : [v.category]).includes(filter));
 }, [safeVideos, filter]);

  const openVideo = (v) => setActive(v);
  const step = (dir) => {
    if (!active) return;
    const idx = filtered.findIndex((v) => v.id === active.id);
    const next = filtered[(idx + dir + filtered.length) % filtered.length];
    setActive(next);
  };

  // Grouped view: when "All" is selected and folders exist, render folder sections
  const groups = useMemo(() => {
    if (filter !== "All" || folders.length === 0) return null;
    const out = folders
      .map((f) => ({ name: f.name, id: f.id, videos: safeVideos.filter((v) => v.folder_id === f.id) }))
      .filter((g) => g.videos.length > 0);
    const unfiled = safeVideos.filter((v) => !v.folder_id || !folders.some((f) => f.id === v.folder_id));
    if (unfiled.length > 0) out.push({ name: "More Work", id: "unfiled", videos: unfiled });
    return out.length > 1 || (out.length === 1 && out[0].id !== "unfiled") ? out : null;
  }, [safeVideos, folders, filter]);

  const renderGrid = (list, keyPrefix = "") => (
    <motion.div layout className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5" data-testid={`video-wall${keyPrefix ? `-${keyPrefix}` : ""}`}>
      <AnimatePresence mode="popLayout">
        {list.map((v, i) => (
          <VideoCard key={v.id} video={v} onOpen={openVideo} featured={i === 0} />
        ))}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <section id="work" className="py-20 md:py-32 border-t border-cream-dark" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-4">
            <div>
              <p className="eyebrow text-ink-soft mb-6">01 / Selected Work</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">Selected Work</h2>
              <p className="mt-4 text-ink-soft max-w-md leading-relaxed">
                A collection of AI-powered UGC ads and creative concepts I've created for brands.
              </p>
            </div>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="portfolio-header-cta" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-ink border-b border-ink/30 pb-1 hover:border-ink transition-colors duration-300">
              Have a Project in Mind <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-[11px] uppercase tracking-[0.14em] font-semibold px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                  filter === c
                    ? "bg-ink text-cream border-ink"
                    : "border-cream-deeper text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={`bg-cream-dark animate-pulse ${i === 3 ? "aspect-video col-span-2" : "aspect-[9/16]"}`} data-testid={`wall-loading-${i}`} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-ink-soft py-16" data-testid="portfolio-empty">No videos in this filter yet.</p>
        ) : groups ? (
          <div className="space-y-14" data-testid="video-wall-grouped">
            {groups.map((g) => (
              <div key={g.id} data-testid={`folder-section-${g.id}`}>
                <div className="flex items-baseline gap-3 mb-6">
                  <h3 className="font-serif text-2xl md:text-3xl text-ink">{g.name}</h3>
                  <span className="text-xs uppercase tracking-[0.16em] text-ink-soft">{g.videos.length} {g.videos.length === 1 ? "clip" : "clips"}</span>
                </div>
                {renderGrid(g.videos, g.id)}
              </div>
            ))}
          </div>
        ) : (
          renderGrid(filtered)
        )}

        <Reveal className="mt-16 md:mt-24 border-t border-cream-dark pt-12 flex flex-wrap items-center justify-between gap-6">
          <p className="font-serif text-2xl md:text-4xl text-ink max-w-xl">
            Want results like these for your brand?
          </p>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-cta"
            className="inline-flex items-center bg-ink hover:bg-neutral-800 text-cream px-8 py-4 rounded-full text-xs uppercase tracking-[0.14em] font-semibold transition-colors duration-300"
          >
            Let's Make Your Next Ad
          </a>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <Lightbox video={active} onClose={() => setActive(null)} onPrev={() => step(-1)} onNext={() => step(1)} />
        )}
      </AnimatePresence>
    </section>
  );
}
